<script setup lang="ts">
import { computed } from 'vue';

import Checkbox from '../Shared/Input/Checkbox/Checkbox.vue';
import AlertTriangleIcon from '@/assets/svg/shared/alert-triangle.svg?component';

import { pluralize } from '@/utils/strings/pluralize.util';
import {
  conversationLinkFor,
  formatWarningNames,
} from '@/core/sourcing/outreach/recent-pro-outreach-warning';
import type { RecentProOutreachWarning } from '@/core/sourcing/outreach/recent-pro-outreach-warning';

const props = defineProps<{
  warning: RecentProOutreachWarning;
  /** Bound to the "exclude these N" checkbox in the some-flagged state. */
  excludeFlagged: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:excludeFlagged', value: boolean): void;
}>();

const excludeFlaggedModel = computed({
  get: () => props.excludeFlagged,
  set: (value: boolean) => emit('update:excludeFlagged', value),
});

const isSingleFlagged = computed(
  () => props.warning.totalSelected === 1 && props.warning.allFlagged,
);
const flaggedNames = computed(() => formatWarningNames(props.warning.flagged));
const singleFlaggedCandidate = computed(() =>
  isSingleFlagged.value ? props.warning.flagged[0] : undefined,
);
const singleConversationLink = computed(() =>
  singleFlaggedCandidate.value?.canLinkToConversation
    ? conversationLinkFor(singleFlaggedCandidate.value)
    : '',
);
const showSingleConversationLink = computed(() => Boolean(singleConversationLink.value));
</script>

<template>
  <div
    class="mt-4 flex gap-2 rounded-xl border border-solid border-caution-100 bg-caution-0 px-4 py-3"
    data-test="repeat-outreach-warning"
  >
    <AlertTriangleIcon class="mt-[1px] h-5 w-5 shrink-0 overflow-visible text-caution-600" />
    <div class="text-sm leading-[21px] text-caution-800">
      <template v-if="isSingleFlagged">
        <span class="font-bold text-caution-800">{{ warning.flagged[0].name }}</span>
        has recent activity with your company.
        <router-link
          v-if="showSingleConversationLink"
          :to="singleConversationLink"
          class="font-semibold text-highlight-600 underline"
        >
          View conversation
        </router-link>
      </template>

      <template v-else-if="warning.allFlagged">
        <span class="font-bold text-caution-800">
          All {{ warning.totalSelected }} selected candidates
        </span>
        have recent activity with your company: {{ flaggedNames }}.
      </template>

      <template v-else>
        <span class="font-bold text-caution-800">
          {{ warning.flagged.length }} of {{ warning.totalSelected }} selected candidates
        </span>
        have recent activity with your company: {{ flaggedNames }}.
        <div class="mt-2 flex items-center gap-2">
          <Checkbox v-model="excludeFlaggedModel" data-test="exclude-flagged-checkbox" />
          <span class="font-semibold text-caution-800">
            Don't include
            {{ warning.flagged.length }}
            {{ pluralize(warning.flagged.length, 'candidate') }}
          </span>
        </div>
      </template>
    </div>
  </div>
</template>
